import { ServerCog, GitBranchPlus, Rocket, Gauge } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import React from "react";
import { ProviderTypes } from "@/types";
import { getProviderMeta } from "@/lib/provider/meta";
import { nonFalse } from "@/lib/utils";

interface StepProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const steps: StepProps[] = [
  {
    icon: <ServerCog size={48} />,
    title: "Connect Server",
    description: `Connect your server or automatically create a new one from ${ProviderTypes.map(
      getProviderMeta
    )
      .filter(nonFalse)
      .filter(p => !p.experimental && p.compute)
      .map(p => p.name)
      .join(", ")}`
  },
  {
    icon: <GitBranchPlus size={48} />,
    title: "Add Your App",
    description:
      "Link your GitHub repository. We support Next.js, React, Python, Go, and everything else with Docker Compose."
  },
  {
    icon: <Rocket size={48} />,
    title: "Deploy",
    description:
      "Click deploy or push to your Git branch. We handle build, deployment, HTTPS, and updates automatically."
  },
  {
    icon: <Gauge size={48} />,
    title: "Monitor",
    description:
      "View logs, check host health and status, manage hosts and backups through our intuitive dashboard."
  }
];

export const HowItWorks = () => {
  return (
    <section id="howItWorks" className="container text-center">
      <svg
        style={{ width: 0, height: 0, position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="icon-gradient" x2="1" y2="1">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="50%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#1d4ed8" />
          </linearGradient>
        </defs>
      </svg>

      <h2 className="text-3xl md:text-4xl font-bold ">
        Start in simple <span className="gradient-light dark:gradient-dark">steps</span>
      </h2>
      <p className="md:w-3/4 mx-auto mt-4 mb-8 text-xl text-muted-foreground">
        From code to live application on your own server in minutes. It&apos;s that easy.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map(({ icon, title, description }: StepProps) => (
          <Card key={title} className="bg-muted/50 border">
            <CardHeader>
              <CardTitle className="text-xl grid gap-4 place-items-center">
                <div className="text-primary">{icon}</div>
                {title}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">{description}</CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};
